import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Edit2, Trash2, Users, Contact, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { CountryCombobox } from '@/components/CountryCombobox';
import KeyContactsTab from './KeyContactsTab';

export default function UsersAdminPage({ embedded = false }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    club_id: '',
    contact_phone: '',
    country: '',
    role: 'standard',
    account_status: 'active'
  });

  // Fetch current user first to decide which tabs are visible
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setCurrentUser(r.data))
      .catch(() => {});
    axios.get(`${API}/venues`, { withCredentials: true })
      .then(r => setVenues(r.data || []))
      .catch(() => {});
  }, []);

  const isSuper = currentUser?.role === 'super';

  // Tabs — standard users only see Key Contacts
  const tabs = useMemo(() => {
    const list = [];
    if (isSuper) list.push({ id: 'users', label: 'User Administration', icon: Users });
    list.push({ id: 'contacts', label: 'Key Contacts', icon: Contact });
    return list;
  }, [isSuper]);

  const [activeTab, setActiveTab] = useState('contacts');
  useEffect(() => {
    // Default super users to the User Administration tab first time we know their role
    if (isSuper) setActiveTab(prev => prev === 'contacts' && users.length === 0 ? 'users' : prev);
  }, [isSuper, users.length]);
  
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API}/clubs`, { withCredentials: true });
      setClubs(response.data);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };
  
  useEffect(() => {
    if (isSuper) fetchUsers();
    fetchClubs();
  }, [isSuper]);
  
  const handleEdit = (user) => {
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      club_id: user.club_id || '',
      contact_phone: user.contact_phone || '',
      country: user.country || '',
      role: user.role,
      account_status: user.account_status || 'active'
    });
    setEditingUser(user);
    setOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/users/${editingUser.user_id}`, formData, { withCredentials: true });
      toast.success('User updated!');
      setOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };
  
  const handleDelete = (user) => {
    setDeleteTarget(user);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deleteTarget.user_id}`, { withCredentials: true });
      toast.success('User deleted successfully');
      fetchUsers();
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to delete user');
      }
    } finally {
      setDeleting(false);
    }
  };
  
  // Render User Administration tab content
  const renderUsersTab = () => (
    <>
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold" style={{ color: '#2D4A3E' }}>User Administration</h2>
            <p className="text-sm text-muted-foreground">Manage user accounts and settings</p>
          </div>
        </div>
      )}
      <Card className="p-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="text-left py-3 px-2 font-semibold">User</th>
              <th className="text-left py-3 px-2 font-semibold">Club</th>
              <th className="text-left py-3 px-2 font-semibold">Phone</th>
              <th className="text-center py-3 px-2 font-semibold">Role</th>
              <th className="text-center py-3 px-2 font-semibold">Status</th>
              <th className="text-center py-3 px-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => {
              const userClub = clubs.find(c => c.club_id === user.club_id);
              return (
                <tr key={user.user_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`user-row-${user.user_id}`}>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                          {(user.first_name || user.name)?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.first_name || user.name} {user.last_name || ''}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-sm">{userClub ? userClub.short_name : '-'}</td>
                  <td className="py-3 px-2 text-sm">{user.contact_phone || '-'}</td>
                  <td className="py-3 px-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.role === 'super' ? 'bg-primary/10 text-primary' : 'bg-secondary/10 text-secondary-foreground'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.account_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.account_status || 'active'}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => { setViewingUser(user); setViewOpen(true); }} className="action-icon" title="View" data-testid={`view-user-${user.user_id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleEdit(user)} className="action-icon" title="Edit" data-testid={`edit-user-${user.user_id}`}>
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(user)} className="action-icon text-red-600 hover:text-red-800" title="Delete" data-testid={`delete-user-${user.user_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
    </>
  );
  
  // Render Key Contacts tab content (real component)
  const renderContactsTab = () => (
    <KeyContactsTab currentUser={currentUser} clubs={clubs} venues={venues} />
  );
  
  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6 fade-in'}>
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#2D4A3E' }}>People</h1>
          <p className="text-muted-foreground mt-1">Manage users and key contacts</p>
        </div>
      )}

      {/* Tab Navigation — hidden when embedded inside People & Places. */}
      {!embedded && (
      <div className="border-b border-stone-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive 
                    ? 'border-green-700 text-green-800 font-medium' 
                    : 'border-transparent text-muted-foreground hover:text-green-700 hover:border-green-300'
                }`}
                style={isActive ? { color: '#2D4A3E' } : {}}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      )}
      
      {/* Tab Content — when embedded, always show Users (Key Contacts
          is rendered separately by the People & Places parent). */}
      <div>
        {(embedded || activeTab === 'users') && renderUsersTab()}
        {!embedded && activeTab === 'contacts' && renderContactsTab()}
      </div>
      
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required data-testid="user-firstname-input" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required data-testid="user-lastname-input" />
              </div>
            </div>
            
            <div>
              <Label>Club - Full Membership</Label>
              <select className="w-full border rounded-lg px-3 py-2" value={formData.club_id} onChange={(e) => setFormData({...formData, club_id: e.target.value})} data-testid="user-club-select">
                <option value="">No Club</option>
                {clubs.map(club => <option key={club.club_id} value={club.club_id}>{club.name}</option>)}
              </select>
            </div>
            
            <div>
              <Label>Contact Phone (NZ Format)</Label>
              <Input value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} placeholder="e.g., 021 123 4567" data-testid="user-phone-input" />
            </div>

            <div>
              <Label>Country</Label>
              <CountryCombobox
                value={formData.country}
                onChange={(v) => setFormData({ ...formData, country: v })}
                testId="user-country"
              />
            </div>
            
            <div>
              <Label>User Type</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="role" value="standard" checked={formData.role === 'standard'} onChange={(e) => setFormData({...formData, role: e.target.value})} data-testid="user-role-standard" />
                  <span>Standard</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="role" value="super" checked={formData.role === 'super'} onChange={(e) => setFormData({...formData, role: e.target.value})} data-testid="user-role-super" />
                  <span>Super User</span>
                </label>
              </div>
            </div>
            
            <div>
              <Label>Account Status</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="status" value="active" checked={formData.account_status === 'active'} onChange={(e) => setFormData({...formData, account_status: e.target.value})} data-testid="user-status-active" />
                  <span>Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="status" value="inactive" checked={formData.account_status === 'inactive'} onChange={(e) => setFormData({...formData, account_status: e.target.value})} data-testid="user-status-inactive" />
                  <span>Inactive</span>
                </label>
              </div>
            </div>
            
            <Button type="submit" className="w-full btn-primary" data-testid="user-submit-button">Update User</Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        itemName={`${deleteTarget?.first_name || deleteTarget?.name || ''} ${deleteTarget?.last_name || ''}`.trim()}
        onConfirm={confirmDelete}
        loading={deleting}
      />

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent data-testid="view-user-dialog">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {viewingUser && (
            <div className="space-y-2 text-sm">
              <div><span className="text-muted-foreground">Name:</span> <strong>{viewingUser.first_name || ''} {viewingUser.last_name || ''}</strong></div>
              <div><span className="text-muted-foreground">Email:</span> {viewingUser.email}</div>
              <div><span className="text-muted-foreground">Phone:</span> {viewingUser.contact_phone || '—'}</div>
              <div><span className="text-muted-foreground">Club:</span> {clubs.find(c => c.club_id === viewingUser.club_id)?.name || '—'}</div>
              <div><span className="text-muted-foreground">Country:</span> {viewingUser.country || '—'}</div>
              <div><span className="text-muted-foreground">Role:</span> {viewingUser.role}</div>
              <div><span className="text-muted-foreground">Status:</span> {viewingUser.account_status || 'active'}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
